import React, { useState, useContext, useEffect } from 'react';
import { Grid2, Typography, MenuItem } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Controller, useFormContext } from 'react-hook-form';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import PaymentAmount from './PaymentAmount';
import BillAmount from './BillAmount';
import PayAmount from './PayAmount';
import PayrollDate from './PayrollDate';
import ExpenseAutocomplete from './ExpenseAutocomplete';
import PaymentTypeMapping from './PaymentType.enum';
import { searchVendors } from '../../Services/ExpenseService';
import { ValidationModeContext } from '../../../contexts/VendorPaymentValidationModeContext';
import { ExpenseClassEnum } from './ExpenseClass.enum';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import CopyIconButtonWithTooltip from '../CopyIconButtonWithTooltip';

const styles = () => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
});

const PaymentDetailsForm = (props) => {
    const { classes, index, expense, isViewOnly, isInvoiceReviewLocked = false } = props;

    const { control, watch, errors, setValue } = useFormContext();
    const [vendor, setVendor] = useState(null);
    const [vendorOptions, setVendorOptions] = useState([]);
    const [payToOrderOfOptions, setPayToOrderOfOptions] = useState([]);
    const [addressOptions, setAddressOptions] = useState([]);
    const validationMode = useContext(ValidationModeContext);

    const formVendorId = watch(`expenseEntries[${index}].vendorId`);
    const formVendorName = watch(`expenseEntries[${index}].vendorName`);
    const formVendorCity = watch(`expenseEntries[${index}].vendorCity`);
    const formVendorState = watch(`expenseEntries[${index}].vendorState`);
    const formVendorZip = watch(`expenseEntries[${index}].vendorZip`);
    const formPaymentVendor = watch(`expenseEntries[${index}].paymentVendor`);
    const formPaymentAddress = watch(`expenseEntries[${index}].paymentAddress`);
    const formVendorPayToOrderOf = watch(`expenseEntries[${index}].vendorPayToOrderOf`);

    // Initialize vendor, address, and pay-to-order-of options when tab changes or data loads
    useEffect(() => {
        const paymentVendor = formPaymentVendor || expense.paymentVendor;
        const vendorId = formVendorId || expense.vendorId;
        const vendorName = formVendorName || expense.vendorName;
        const vendorCity = formVendorCity || expense.vendorCity;
        const vendorState = formVendorState || expense.vendorState;
        const vendorZip = formVendorZip || expense.vendorZip;
        const paymentAddress = formPaymentAddress || expense.paymentAddress;
        const vendorPayToOrderOf = formVendorPayToOrderOf || expense.vendorPayToOrderOf;

        if (vendorId && vendorName) {
            // Prefer existing paymentVendor object (has correct IDs), otherwise construct from fields
            let vendorObj;
            if (paymentVendor && typeof paymentVendor === 'object') {
                vendorObj = paymentVendor;
            } else {
                vendorObj = {
                    id: vendorId,
                    supplierId: vendorId,
                    supplierName: vendorName,
                    city: vendorCity,
                    state: vendorState,
                    postalCode: vendorZip,
                    vendorInfo: `${vendorName}, ${vendorCity}, ${vendorState} ${vendorZip}`,
                };
            }

            setValue(`expenseEntries[${index}].paymentVendor`, vendorObj);
            setVendor(vendorObj);
            setVendorOptions([vendorObj]);

            setValue(`expenseEntries[${index}].vendorCity`, vendorCity);
            setValue(`expenseEntries[${index}].vendorState`, vendorState);
            setValue(`expenseEntries[${index}].vendorZip`, vendorZip);

            if (vendorPayToOrderOf) {
                setPayToOrderOfOptions([
                    {
                        payToOrderOf: vendorPayToOrderOf,
                        city: vendorCity,
                        state: vendorState,
                        postalCode: vendorZip,
                    },
                ]);
                setValue(`expenseEntries[${index}].vendorPayToOrderOf`, vendorPayToOrderOf);
            }

            if (paymentAddress && typeof paymentAddress === 'object') {
                setAddressOptions([{ ...paymentAddress }]);
                setValue(`expenseEntries[${index}].paymentAddress`, paymentAddress);

                // Update city/state/zip if address has different values
                if (paymentAddress.city || paymentAddress.state || paymentAddress.postalCode) {
                    setValue(
                        `expenseEntries[${index}].vendorCity`,
                        paymentAddress.city || vendorCity
                    );
                    setValue(
                        `expenseEntries[${index}].vendorState`,
                        paymentAddress.state || vendorState
                    );
                    setValue(
                        `expenseEntries[${index}].vendorZip`,
                        paymentAddress.postalCode || vendorZip
                    );
                }
            } else if (!paymentAddress && vendorId && vendorName) {
                // If no paymentAddress, search vendor to reconstruct address
                searchVendors(vendorName)
                    .then((response) => {
                        const vendorData = response.find((v) => v.id === vendorId);
                        if (vendorData) {
                            const reconstructedAddress = {
                                id: vendorData.id,
                                addressLine1: vendorData.addressLine1,
                                addressLine2: vendorData.addressLine2,
                                city: vendorData.city,
                                state: vendorData.state,
                                postalCode: vendorData.postalCode,
                            };
                            setAddressOptions([reconstructedAddress]);
                            setValue(
                                `expenseEntries[${index}].paymentAddress`,
                                reconstructedAddress
                            );
                        }
                    })
                    .catch(() => {});
            }
        }
    }, [index]);
    const currentPaymentType = watch(`expenseEntries[${index}].paymentType`);
    const currentExpenseCategory = watch(`expenseEntries[${index}].category`);
    const currentExpenseClass = watch(`expenseEntries[${index}].expenseClass`);
    const currentExpenseStatus = watch(`expenseEntries[${index}].status`);

    const validateField = (value) =>
        (validationMode === 'submit' ||
            currentExpenseStatus === ExpenseStatusValuesEnum.Approved) &&
        !value;

    const shouldShowAmountConditionMsg =
        (Number(watch(`expenseEntries[${index}].billAmount`)) === 0 &&
            Number(watch(`expenseEntries[${index}].payAmount`)) === 0) ||
        (!watch(`expenseEntries[${index}].billAmount`) &&
            !watch(`expenseEntries[${index}].payAmount`));

    const shouldRenderSupplementalFields =
        currentExpenseCategory === 'Supplemental' &&
        currentExpenseClass === ExpenseClassEnum.Payment;

    const isMoneyOrder = currentPaymentType === PaymentTypeMapping.MoneyOrder.value;

    const getVendorOptions = async (searchText) => {
        const response = await searchVendors(searchText);
        const result = response
            .filter(
                (vendorEntry) =>
                    vendorEntry.supplierStatus === 'Active' || vendorEntry.supplierStatus === 'Hold'
            )
            .map((vendorEntry) => ({
                ...vendorEntry,
                vendorInfo: `${vendorEntry.supplierName}, ${vendorEntry.city}, ${vendorEntry.state} ${vendorEntry.postalCode}`,
            }))
            .sort((a, b) => a.vendorInfo.localeCompare(b.vendorInfo));
        setVendorOptions(result);
        return result;
    };

    const getPayToOrderOfOptions = (selectedVendor) => {
        if (selectedVendor == null) {
            return [];
        }
        const options = vendorOptions.filter(
            (option) =>
                option.supplierName === selectedVendor.supplierName &&
                option.payToOrderOf !== null &&
                option.payToOrderOf.trim() !== ''
        );
        setPayToOrderOfOptions(options);

        if (options.length === 0) {
            setAddressOptions([selectedVendor]);
        }
    };

    const getAddressOptions = (selectedPayToOrderOf) => {
        if (selectedPayToOrderOf == null) {
            setAddressOptions([]);
            return [];
        }
        const options = vendorOptions.filter(
            (option) => option.payToOrderOf === selectedPayToOrderOf
        );
        setAddressOptions(options);
    };

    const resetPayToOrderOfOptions = () => {
        setPayToOrderOfOptions([]);
        setValue(`expenseEntries[${index}].vendorPayToOrderOf`, '');
    };

    const updatePaymentFields = (value, validate = true) => {
        if (value) {
            setValue(`expenseEntries[${index}].vendorCity`, value.city, {
                shouldValidate: validate,
            });
            setValue(`expenseEntries[${index}].vendorState`, value.state, {
                shouldValidate: validate,
            });
            setValue(`expenseEntries[${index}].vendorZip`, value.postalCode, {
                shouldValidate: validate,
            });
        }
    };

    const selectVendor = (selectedVendor) => {
        resetPayToOrderOfOptions();
        setAddressOptions([]);
        setVendor(selectedVendor);

        if (selectedVendor) {
            setValue(`expenseEntries[${index}].vendorId`, selectedVendor.id);
            setValue(`expenseEntries[${index}].vendorName`, selectedVendor.supplierName);
            setValue(`expenseEntries[${index}].vendorCity`, selectedVendor.city);
            setValue(`expenseEntries[${index}].vendorState`, selectedVendor.state);
            setValue(`expenseEntries[${index}].vendorZip`, selectedVendor.postalCode);
            setValue(`expenseEntries[${index}].paymentType`, selectedVendor.defaultPaymentType);
        }

        getPayToOrderOfOptions(selectedVendor);
        if (payToOrderOfOptions.length < 2) {
            updatePaymentFields(selectedVendor);
        }
    };

    const selectPayToOrderOf = (selection) => {
        if (selection) {
            setValue(`expenseEntries[${index}].vendorPayToOrderOf`, selection.payToOrderOf || '');
            getAddressOptions(selection.payToOrderOf);
        }
    };

    const updatePaymentTotalAmount = () => {
        setValue(
            `expenseEntries[${index}].amount`,
            Number(
                (
                    Number(watch(`expenseEntries[${index}].payAmount`)) +
                    Number(watch(`expenseEntries[${index}].billAmount`))
                ).toFixed(2)
            )
        );
    };

    const isParentExpenseEntry = index === 0;
    const isPaymentFieldLocked = isViewOnly || isInvoiceReviewLocked;
    const shouldShowCopyBillAmount = isParentExpenseEntry && !isPaymentFieldLocked;
    const billAmountValue = watch(`expenseEntries[${index}].billAmount`);
    const copyBillAmountIntoPayAmount = () => {
        if (billAmountValue) {
            setValue(`expenseEntries[${index}].payAmount`, billAmountValue, {
                shouldDirty: true,
                shouldValidate: true,
            });
            updatePaymentTotalAmount();
        }
    };

    return (
        <>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                Payment Details
            </Typography>
            {shouldRenderSupplementalFields ? (
                <Grid2 container spacing={3}>
                    <BillAmount
                        index={index}
                        expense={expense}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                        shouldShowAmountConditionMsg={shouldShowAmountConditionMsg}
                        updatePaymentTotalAmount={updatePaymentTotalAmount}
                        isUsdOnly
                    />
                    {shouldShowCopyBillAmount && (
                        <CopyIconButtonWithTooltip
                            title="Copy Bill Amount"
                            onClick={copyBillAmountIntoPayAmount}
                            disabled={!billAmountValue}
                        />
                    )}
                    <PayAmount
                        index={index}
                        expense={expense}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                        shouldShowAmountConditionMsg={shouldShowAmountConditionMsg}
                        updatePaymentTotalAmount={updatePaymentTotalAmount}
                        isUsdOnly
                    />
                    <PayrollDate
                        expense={expense}
                        index={index}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                        isRequired={currentExpenseStatus === ExpenseStatusValuesEnum.Approved}
                    />
                </Grid2>
            ) : (
                <Grid2 container spacing={3}>
                    <Grid2 item size={8}>
                        <Controller
                            control={control}
                            name={`expenseEntries[${index}].paymentVendor`}
                            rules={{
                                validate: (value) => {
                                    if (validateField(value)) {
                                        return 'Vendor is required';
                                    }
                                    return true; // Passes in save mode
                                },
                            }}
                            render={({ onChange, value }) => (
                                <ExpenseAutocomplete
                                    id="payment-vendor-search"
                                    onChange={(e) => {
                                        selectVendor(e);
                                        if (e) {
                                            const vendorWithInfo = {
                                                ...e,
                                                vendorInfo:
                                                    e.vendorInfo ||
                                                    `${e.supplierName}, ${e.city}, ${e.state} ${e.postalCode}`,
                                            };
                                            onChange(vendorWithInfo);
                                        } else {
                                            onChange(e);
                                        }
                                    }}
                                    value={value}
                                    inputProps={{ disableUnderline: true }}
                                    minLength={3}
                                    label="Vendor"
                                    required
                                    error={Boolean(errors.expenseEntries?.[index]?.paymentVendor)}
                                    helperText={
                                        errors.expenseEntries?.[index]?.paymentVendor?.message
                                    }
                                    getOptionsMethod={getVendorOptions}
                                    placeholder="Search by name, city, or zip"
                                    optionLabelKey="vendorInfo"
                                    optionSelectedKey="supplierId"
                                    showRefreshVendorButton
                                    isViewOnly={isPaymentFieldLocked}
                                />
                            )}
                        />
                    </Grid2>
                    {(payToOrderOfOptions.length > 0 || expense.vendorPayToOrderOf) && (
                        <Grid2 item size={8}>
                            <Controller
                                control={control}
                                name={`expenseEntries[${index}].vendorPayToOrderOf`}
                                rules={{
                                    validate: (value) => {
                                        if (validateField(value)) {
                                            return 'Pay to the order of is required';
                                        }
                                        return true; // Passes in save mode
                                    },
                                }}
                                render={({ onChange, value }) => (
                                    <ExpenseTextField
                                        id="pay-to-order-of-search"
                                        select
                                        onChange={(e) => {
                                            const selectedPayToOrderOf = e.target.value;
                                            const fullOption = payToOrderOfOptions.find(
                                                (opt) => opt.payToOrderOf === selectedPayToOrderOf
                                            );
                                            selectPayToOrderOf(fullOption);
                                            onChange(selectedPayToOrderOf);
                                        }}
                                        value={value ?? ''}
                                        label="Pay to the order of"
                                        placeholder="Select"
                                        required
                                        error={Boolean(
                                            errors.expenseEntries?.[index]?.vendorPayToOrderOf
                                        )}
                                        helperText={
                                            errors.expenseEntries?.[index]?.vendorPayToOrderOf
                                                ?.message
                                        }
                                        disabled={
                                            isPaymentFieldLocked ||
                                            vendor == null ||
                                            payToOrderOfOptions.length === 0
                                        }
                                    >
                                        <MenuItem value="" disabled>
                                            Select
                                        </MenuItem>
                                        {payToOrderOfOptions.map((option, i) => (
                                            <MenuItem
                                                key={`${option.payToOrderOf}-${
                                                    option.postalCode || ''
                                                }`}
                                                value={option.payToOrderOf}
                                                id={`provider-search-option-${i}`}
                                            >
                                                {`${option.payToOrderOf}, ${option.city}, ${option.state} ${option.postalCode}`}
                                            </MenuItem>
                                        ))}
                                    </ExpenseTextField>
                                )}
                            />
                        </Grid2>
                    )}
                    <PaymentAmount
                        index={index}
                        expense={expense}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                    />
                    {isMoneyOrder && (
                        <Grid2 item size={12}>
                            <Controller
                                control={control}
                                name={`expenseEntries[${index}].paymentAddress`}
                                rules={{
                                    validate: (value) => {
                                        if (validateField(value)) {
                                            return 'Address is required';
                                        }
                                        return true; // Passes in save mode
                                    },
                                }}
                                render={({ onChange, value }) => {
                                    // Match by id, addressLine1, or combination of city+state+postalCode/zip
                                    const matchedOption =
                                        value && typeof value === 'object'
                                            ? addressOptions.find(
                                                  (opt) =>
                                                      opt.id === value.id ||
                                                      opt.addressLine1 === value.addressLine1 ||
                                                      (opt.city === value.city &&
                                                          opt.state === value.state &&
                                                          (opt.postalCode === value.postalCode ||
                                                              opt.postalCode === value.zip))
                                              )
                                            : value;

                                    return (
                                        <ExpenseTextField
                                            id="address-name"
                                            select
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                onChange(newValue);
                                                if (newValue && typeof newValue === 'object') {
                                                    updatePaymentFields(newValue);
                                                }
                                            }}
                                            value={matchedOption ?? ''}
                                            label="Address"
                                            required
                                            disabled={
                                                isPaymentFieldLocked ||
                                                vendor == null ||
                                                addressOptions.length < 1
                                            }
                                            error={Boolean(
                                                errors.expenseEntries?.[index]?.paymentAddress
                                            )}
                                            helperText={
                                                errors.expenseEntries?.[index]?.paymentAddress
                                                    ?.message
                                            }
                                        >
                                            <MenuItem value="" disabled>
                                                Select
                                            </MenuItem>
                                            {addressOptions.map((option, i) => (
                                                <MenuItem
                                                    key={`${option.addressLine1}-${
                                                        option.postalCode || ''
                                                    }`}
                                                    value={option}
                                                    id={`address-option-${i}`}
                                                >
                                                    {option.addressLine1}
                                                </MenuItem>
                                            ))}
                                        </ExpenseTextField>
                                    );
                                }}
                            />
                        </Grid2>
                    )}
                </Grid2>
            )}
        </>
    );
};

export default withStyles(styles)(PaymentDetailsForm);
